// ABOUTME: Type definitions for AdminChildDetail component and its subcomponents
// ABOUTME: Includes child data structures, family info, form states, and validation utilities

import dayjs, { Dayjs } from 'dayjs';
import { BehaviorType } from '../../types/api';

export interface AdminFamilyInfo {
  id: number;
  name: string;
  role: string;
  joinedAt: string;
  childrenCount: number;
  timezone?: string;
}

export interface ChildDetail {
  id: number;
  name: string;
  aliases?: string[];
  birthYear: number;
  birthMonth?: number;
  birthDay?: number;
  age: number;
  gender?: string;
  profileImageUrl?: string;
  mantra?: string;
  familyLoveQuestion?: string;
  familyNeedQuestion?: string;
  createdAt: string;
  updatedAt: string;
  family: AdminFamilyInfo;
  behaviorRecordCount: number;
  lastBehaviorRecord?: string;
}

// Form State Types

export interface EditEventFormState {
  behaviorType: BehaviorType;
  notes: string;
  childId: number;
  newEventTime: Dayjs | null;
}

export interface EditChildFormState {
  childName: string;
  aliases: string[];
  birthYear: number;
  birthMonth: string;
  gender: string;
  mantra: string;
  familyLoveQuestion: string;
  familyNeedQuestion: string;
}

/**
 * Raw behavior log event as returned by GET /api/admin/children/{id}/behavior-log.
 * The backend has historically sent a mix of camelCase and PascalCase field names
 * for the time-related fields, hence the fallback pairs below.
 */
export interface RawBehaviorLogEvent {
  behaviorId?: number;
  id?: number;
  childId: number;
  childName?: string;
  familyName?: string;
  behaviorType?: number;
  type?: number;
  notes?: string;
  description?: string;
  recordedAt?: string;
  RecordedAt?: string;
  eventTime?: string;
  EventTime?: string;
  eventTimezone?: string;
  EventTimezone?: string;
  eventLocalTime?: string;
  EventLocalTime?: string;
  recordedByUserName?: string;
  loggedBy?: string;
  recordedByUserId?: number;
  recordingSource?: string;
}

// Utility Functions

export const mapRecordingSourceToDisplayName = (source: string | undefined): string => {
  if (!source) return 'Unknown';

  switch (source) {
    case 'Unknown':
      return 'Unknown';
    case 'WebApplicationButton':
      return 'Web App (Button)';
    case 'Alexa':
      return 'Alexa';
    case 'WebApplicationVoice':
      return 'Web App (Voice)';
    case 'Siri':
      return 'Siri';
    default:
      return source;
  }
};

// Validation functions for event date editing
export const validateEventDate = (date: string, familyCreatedAt: string): boolean => {
  const eventDate = dayjs(date);
  const now = dayjs();
  const familyDate = dayjs(familyCreatedAt);

  return !eventDate.isAfter(now) && !eventDate.isBefore(familyDate);
};

export const getDateValidationError = (date: string, familyCreatedAt: string, originalRecordedAt?: string): string | null => {
  const eventDate = dayjs(date);
  const now = dayjs();
  const familyDate = dayjs(familyCreatedAt);

  if (eventDate.isAfter(now)) {
    return 'Sorry, no time machines allowed! Events can\'t be logged in the future.';
  }

  if (eventDate.isBefore(familyDate)) {
    return 'Event time cannot be earlier than your family was created.';
  }

  // When editing an existing event, prevent moving it later than it was originally recorded
  if (originalRecordedAt) {
    const recordedDate = dayjs(originalRecordedAt);
    if (eventDate.isAfter(recordedDate)) {
      return 'Events cannot be moved to a time after they were originally logged.';
    }
  }

  return null;
};
